import { request } from "./transport";

// Admin authoring shape — distinct from `Book` / `Chapter` /
// `Article` in `@/types/api`, which are the locale-resolved reader
// shapes. Admins author both en + zh side-by-side and need the raw
// fields, plus draft/published status and counts.

export interface AdminBook {
  readonly id: string;
  readonly slug: string;
  readonly title_en: string;
  readonly title_zh: string;
  readonly subtitle_en: string;
  readonly subtitle_zh: string;
  readonly cover_image_url: string | null;
  readonly status: "draft" | "published";
  readonly sort_order: number;
  readonly published_at: string | null;
  readonly chapter_count: number;
  readonly article_count: number;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface AdminChapter {
  readonly id: string;
  readonly book_id: string;
  readonly slug: string;
  readonly title_en: string;
  readonly title_zh: string;
  readonly sort_order: number;
  readonly article_count: number;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface AdminArticle {
  readonly id: string;
  readonly chapter_id: string;
  readonly slug: string;
  readonly title_en: string;
  readonly title_zh: string;
  readonly body_md_en: string;
  readonly body_md_zh: string;
  readonly accent: string | null;
  readonly prev_question: string | null;
  readonly sort_order: number;
  readonly source_locale: string | null;
  readonly source_route: string | null;
  readonly source_model: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface AdminBookCreateInput {
  title: string;
  slug?: string;
}

export interface AdminBookPatch {
  title?: string;
  subtitle?: string;
  title_zh?: string;
  subtitle_zh?: string;
  slug?: string;
  cover_image_url?: string | null;
  sort_order?: number;
  status?: "draft" | "published";
}

export interface AdminChapterCreateInput {
  title: string;
  slug?: string;
}

export interface AdminChapterPatch {
  title?: string;
  title_zh?: string;
  slug?: string;
  sort_order?: number;
}

export interface AdminArticleCreateInput {
  chapter_id: string;
  title?: string;
  title_zh?: string;
  body_md?: string;
  body_md_zh?: string;
}

export interface AdminArticlePatch {
  title?: string;
  title_zh?: string;
  body_md?: string;
  body_md_zh?: string;
  sort_order?: number;
  chapter_id?: string;
  accent?: string;
  prev_question?: string;
  slug?: string;
}

const BASE = "/admin/library";

export const adminLibrary = {
  listAdminBooks: () => request<{ books: AdminBook[] }>(`${BASE}/books`),

  getAdminBook: (id: string) =>
    request<{ book: AdminBook }>(`${BASE}/books/${id}`),

  createAdminBook: (input: AdminBookCreateInput) =>
    request<{ book: AdminBook }>(`${BASE}/books`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  updateAdminBook: (id: string, patch: AdminBookPatch) =>
    request<{ book: AdminBook }>(`${BASE}/books/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  deleteAdminBook: (id: string) =>
    request<{ ok: true }>(`${BASE}/books/${id}`, { method: "DELETE" }),

  publishAdminBook: (id: string) =>
    request<{ book: AdminBook }>(`${BASE}/books/${id}/publish`, {
      method: "POST",
    }),

  unpublishAdminBook: (id: string) =>
    request<{ book: AdminBook }>(`${BASE}/books/${id}/publish`, {
      method: "DELETE",
    }),

  listAdminChapters: (bookId: string) =>
    request<{ chapters: AdminChapter[] }>(`${BASE}/books/${bookId}/chapters`),

  createAdminChapter: (bookId: string, input: AdminChapterCreateInput) =>
    request<{ chapter: AdminChapter }>(`${BASE}/books/${bookId}/chapters`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  updateAdminChapter: (bookId: string, id: string, patch: AdminChapterPatch) =>
    request<{ chapter: AdminChapter }>(
      `${BASE}/books/${bookId}/chapters/${id}`,
      { method: "PATCH", body: JSON.stringify(patch) },
    ),

  deleteAdminChapter: (bookId: string, id: string) =>
    request<{ ok: true }>(`${BASE}/books/${bookId}/chapters/${id}`, {
      method: "DELETE",
    }),

  listAdminArticles: (bookId: string) =>
    request<{ articles: AdminArticle[] }>(`${BASE}/books/${bookId}/articles`),

  getAdminArticle: (bookId: string, id: string) =>
    request<{ article: AdminArticle }>(
      `${BASE}/books/${bookId}/articles/${id}`,
    ),

  createAdminArticle: (bookId: string, input: AdminArticleCreateInput) =>
    request<{ article: AdminArticle }>(`${BASE}/books/${bookId}/articles`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  updateAdminArticle: (bookId: string, id: string, patch: AdminArticlePatch) =>
    request<{ article: AdminArticle }>(
      `${BASE}/books/${bookId}/articles/${id}`,
      { method: "PATCH", body: JSON.stringify(patch) },
    ),

  deleteAdminArticle: (bookId: string, id: string) =>
    request<{ ok: true }>(`${BASE}/books/${bookId}/articles/${id}`, {
      method: "DELETE",
    }),
};
